/**
 * CardIssuanceSection — post-commit debit-card print + activate ceremony.
 *
 * PORT of the V2 SPFx CardIssuancePanel (SPFx CardIssuancePanel port,
 * Collette Cruse 3/27/2026 workflow, Bill 2026-07-10: "We had a pretty
 * sweet Print Debit Card routine set up in the SPFx app"). Rendered inside
 * CommitCompletionPanel once the pipeline reaches `committed`.
 *
 * Documented workflow (Collette Cruse 3/27/2026, from the SPFx header):
 *   1. EFTCardAdd creates card in WARM status (post-sign pipeline does this)
 *   2. FSR opens Entrust → enters account number → selects card → prints
 *   3. Entrust generates PIN
 *   4. FSR activates card (POST /v3/carts/{cartId}/cards/activate →
 *      EFTCardMod sets status Active; minors get $510/$510 limits,
 *      adults $1,010/$5,010 — Collette 4/7/2026, applied server-side)
 *   5. Customer calls 800# (1-800-992-3808) to set their own PIN
 *
 * Data flow: GET /v3/carts/{cartId}/cards (V3CardIssuanceFunction reads the
 * pipeline snapshot's done EFTCardAdd steps — the v3 equivalent of the SPFx
 * extractCardInfo over pipeline steps). Card PANs are masked last-4 ONLY:
 * the full PAN never exits the Function (PCI decision, Bill 2026-05-08 —
 * Entrust pulls the card by account #, shown FULL here for the FSR).
 *
 * v3 delta vs SPFx: the SPFx "Skip" button created an ao_Tasks follow-up;
 * in v3 the 'debit-card-print' follow-up task is ALREADY auto-created at
 * commit (PipelineResumeFunction.CreateCompletionFollowUpTasksAsync), and a
 * successful Activate auto-completes it server-side — so skipping is simply
 * closing this panel; the open task keeps tracking the card.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { app } from '@microsoft/teams-js';
import type { IssuedCard, Iso8601 } from '@retail-ops/shared-types';
import { Banner, Button, ModalShell, Pill } from '@/components/primitives';
import { useApi } from './hooks/useApi';
import { useConfig } from '@/contexts/ConfigContext';
import { useAuth } from '@/contexts/AuthContext';

export interface CardIssuanceSectionProps {
  cartId: string;
  /** Raised after load so the parent can hide its generic debit-card reminder. */
  onCardsLoaded?: (count: number) => void;
  /** Raised after a successful activation (server also completed the follow-up task). */
  onActivated?: () => void;
}

/**
 * Entrust workflow steps — copy VERBATIM from SPFx CardIssuancePanel
 * (Collette Cruse 3/27/2026). Step 2 interpolates the account number.
 */
const ENTRUST_STEPS = (acctId: string, designName?: string): ReadonlyArray<string> => [
  'Open Entrust on your workstation',
  `Enter account number: ${acctId}`,
  'Select the card from the list that appears',
  // Name the chosen design in the step itself — this is the one Entrust action the customer's
  // choice depends on, and Entrust never receives the field (Bill 2026-07-16).
  designName
    ? `Choose card image: ${designName}`
    : 'Choose card image (customer preference)',
  'Generate PIN (always generate)',
  'Place checkmark and hit "Process"',
  'Wait for card to print',
];

export function CardIssuanceSection({
  cartId,
  onCardsLoaded,
  onActivated,
}: CardIssuanceSectionProps): JSX.Element | null {
  const { client, makeSignal } = useApi();
  const { config } = useConfig();
  const { user } = useAuth();

  // ── Branch-specific Entrust launch link (WT-BACKLOG B1) ────────────────
  // The FSR's ACTIVE branch (home branch, or today's temporary override —
  // both resolved into user.activeBranchCode) governs which Entrust endpoint
  // opens. Falls back to the legacy `branchCode` alias. `entrustUrl` is
  // deferred/nullable: when a branch has no URL configured yet, we keep the
  // manual Entrust steps and show no launch button (never fabricate a URL).
  const activeBranch = useMemo(() => {
    const code = user?.activeBranchCode ?? user?.branchCode;
    if (!code) return undefined;
    return config.branches.find((b) => b.branchCode === code);
  }, [user?.activeBranchCode, user?.branchCode, config.branches]);
  const entrustUrl = activeBranch?.entrustUrl?.trim() || undefined;
  const branchLabel = activeBranch?.branchName ?? user?.activeBranchCode ?? user?.branchCode ?? 'your branch';

  // Open the branch's Entrust link via the Teams SDK (never window.open / a
  // bare anchor — Teams desktop/iOS capture target=_blank in the webview).
  // Mirrors useStateGovLink.openInBrowser.
  const openEntrust = useCallback((): void => {
    if (!entrustUrl) return;
    try {
      const maybe = (app as unknown as { openLink?: (url: string) => Promise<void> }).openLink;
      if (typeof maybe === 'function') { void maybe(entrustUrl); return; }
      if (typeof window !== 'undefined') window.open(entrustUrl, '_blank', 'noopener,noreferrer');
    } catch {
      if (typeof window !== 'undefined') window.open(entrustUrl, '_blank', 'noopener,noreferrer');
    }
  }, [entrustUrl]);

  const [cards, setCards] = useState<ReadonlyArray<IssuedCard>>([]);
  const [modalCard, setModalCard] = useState<IssuedCard | null>(null);
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // ── Load issued cards (fail-soft — the panel never blocks completion) ──
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await client.retail.carts.cards(cartId, { signal: makeSignal() });
        if (cancelled || !resp.success) return;
        const list = resp.cards ?? [];
        setCards(list);
        onCardsLoaded?.(list.length);
      } catch {
        // Fail-soft: no cards section is a valid render.
      }
    })();
    return () => { cancelled = true; };
    // onCardsLoaded intentionally not a dep — parent passes an inline setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, makeSignal, cartId]);

  const productName = (code?: string): string => {
    if (!code) return '';
    const hit = config.products.find((p) => p.code === code);
    return hit?.name ?? code;
  };

  const handleCopyAcct = (acctId: string): void => {
    if (!navigator.clipboard || !acctId) return;
    navigator.clipboard.writeText(acctId).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }).catch(() => { /* ignore */ });
  };

  // Card design artwork URL for the Entrust "choose card image" step — the FSR matches the
  // physical card to what they see here (Bill 2026-07-17: put the preview in the modal's
  // white space for recognition). Served from the same anonymous image route the cart
  // gallery uses; undefined for programs with no artwork (falls back to no image).
  const cardImageUrl = (code?: string): string | undefined => {
    if (!code) return undefined;
    const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
    return `${base}/v3/config/card-designs/${encodeURIComponent(code)}/image`;
  };

  // ── Activate (workflow step 4 — "Card Printed — Activate Now") ─────────
  const handleActivate = useCallback(async (card: IssuedCard): Promise<void> => {
    if (activating) return;
    setActivating(true);
    setActivateError(null);
    try {
      // Bypass key convention per DeathNotice.tsx — empty string rides the
      // bearer token (AdminAuthService role-claim path, Wave 13).
      const bypassKey = (import.meta.env.VITE_ADMIN_BYPASS_KEY as string | undefined) ?? '';
      const resp = await client.retail.carts.activateCard(bypassKey, cartId, {
        cifId: card.cifId,
        acctId: card.acctId,
      });
      if (!resp.success) {
        setActivateError(
          resp.errorCode ? `${resp.errorCode}: ${resp.error ?? 'Activation failed.'}` : (resp.error ?? 'Activation failed.'),
        );
        return;
      }
      const activatedAt = resp.activatedAt ?? (new Date().toISOString() as Iso8601);
      setCards((prev) => prev.map((c) =>
        c.cifId === card.cifId && c.acctId === card.acctId
          ? { ...c, activated: true, activatedAt }
          : c));
      setModalCard((prev) =>
        prev && prev.cifId === card.cifId && prev.acctId === card.acctId
          ? { ...prev, activated: true, activatedAt }
          : prev);
      onActivated?.();
    } catch (err) {
      setActivateError(err instanceof Error ? err.message : 'Activation failed.');
    } finally {
      setActivating(false);
    }
  }, [activating, client, cartId, onActivated]);

  // SPFx parity: no cards → no section.
  if (cards.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <span
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          fontWeight: 600,
          color: 'var(--ink-500)',
        }}
      >
        Debit card issuance
      </span>
      <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>
        Cards created in Core Director. Print each card in Entrust, then activate below.
      </div>

      {cards.map((card) => (
        <div
          key={`${card.cifId}:${card.acctId}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--sp-4)',
            padding: 'var(--sp-3) var(--sp-4)',
            border: card.activated
              ? '1px solid var(--pcb-green)'
              : '1px solid var(--surface-border)',
            borderRadius: 'var(--r-md)',
            background: card.activated ? 'var(--ok-bg)' : 'transparent',
            fontSize: 13,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--sp-3)' }}>
              <span style={{ fontWeight: 600 }}>{card.personName}</span>
              {/* Minor limits pill — SPFx CardIssuancePanel (Collette 4/7/2026). */}
              {card.isMinor && <Pill variant="info">Minor $510/$510</Pill>}
              {card.linkedExisting && <Pill variant="muted">Linked to existing card</Pill>}
              {productName(card.productCode) && (
                <span style={{ color: 'var(--ink-500)' }}>{productName(card.productCode)}</span>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                marginTop: 'var(--sp-2)',
                fontSize: 12,
                fontFamily: 'var(--font-mono, monospace)',
                color: 'var(--ink-500)',
              }}
            >
              <span>{card.cardNumMasked ?? 'Card on file'}</span>
              {card.activated
                ? <Pill variant="ok">ACTIVE</Pill>
                : <Pill variant="warn">WARM</Pill>}
            </div>
          </div>
          <Button
            variant={card.activated ? 'ghost' : 'primary'}
            size="sm"
            disabled={card.activated}
            onClick={() => {
              setModalCard(card);
              setActivateError(null);
              // Auto-copy the account number when the dialog opens (Bill 2026-07-17): the
              // FSR pastes it straight into Entrust step 2 without a second click.
              handleCopyAcct(card.acctId);
            }}
            style={{ flexShrink: 0 }}
          >
            {card.activated ? 'Issued' : 'Issue card'}
          </Button>
        </div>
      ))}

      {/* v3 delta vs the SPFx Skip button: the 'debit-card-print' follow-up
          task already exists (auto-created at commit) and stays open until
          activation completes it. */}
      {cards.some((c) => !c.activated) && (
        <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>
          Not printing right now? The &ldquo;Print debit card&rdquo; follow-up task below stays
          open until the card is activated.
        </div>
      )}

      {/* ── Issue Card modal — ported step-for-step from the SPFx panel ── */}
      {modalCard && (
        <ModalShell
          open
          onClose={() => setModalCard(null)}
          title={`Issue Debit Card — ${modalCard.personName}`}
          size="md"
          footer={
            modalCard.activated ? (
              <Button variant="green" onClick={() => setModalCard(null)}>Done</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setModalCard(null)}>Cancel</Button>
                <Button
                  variant="green"
                  disabled={activating}
                  onClick={() => { void handleActivate(modalCard); }}
                >
                  {activating ? 'Activating…' : 'Card Printed — Activate Now'}
                </Button>
              </>
            )
          }
        >
          {modalCard.activated ? (
            <div style={{ textAlign: 'center', padding: 'var(--sp-6)' }}>
              <div style={{ fontSize: 48, marginBottom: 'var(--sp-3)', color: 'var(--pcb-green)' }}>&#10003;</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--pcb-green)', marginBottom: 'var(--sp-2)' }}>
                Card Activated
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-500)' }}>
                Give customer the 800 number to set their PIN.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
              {/* Card details */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 'var(--sp-4)',
                  padding: 'var(--sp-4)',
                  background: 'var(--surface-muted, var(--surface-card))',
                  border: '1px solid var(--surface-border)',
                  borderRadius: 'var(--r-md)',
                  fontSize: 13,
                }}
              >
                <div>
                  <DetailLabel text="Cardholder" />
                  <div style={{ fontWeight: 600 }}>{modalCard.personName}</div>
                </div>
                <div>
                  <DetailLabel text="Product" />
                  <div>{productName(modalCard.productCode) || '—'}</div>
                </div>
                {/* Card design — the image the FSR must pick BY HAND in Entrust (it doesn't
                    receive the field). Without this the "Choose card image" step below is a
                    guess (Bill 2026-07-16). */}
                <div>
                  <DetailLabel text="Card design" />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                    {cardImageUrl(modalCard.cardDesignCode) && (
                      <img
                        src={cardImageUrl(modalCard.cardDesignCode)}
                        alt={`${modalCard.cardDesignName ?? 'Card'} design preview`}
                        style={{
                          width: 108, height: 68, objectFit: 'cover', borderRadius: 6,
                          border: '1px solid var(--ink-100)', flex: '0 0 auto',
                          background: 'var(--surface-2, #f1f5f9)',
                        }}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    <div style={{ fontWeight: 600 }}>
                      {modalCard.cardDesignName
                        ? `${modalCard.cardDesignName}${modalCard.cardDesignCode ? ` (${modalCard.cardDesignCode})` : ''}`
                        : modalCard.cardDesignCode
                          ? `Code ${modalCard.cardDesignCode}`
                          : '—'}
                    </div>
                  </div>
                </div>
                <div>
                  <DetailLabel text="Account number" />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                    {/* FULL account number — banker surface (Entrust pulls the
                        card by account #, Bill 2026-05-08). */}
                    <span style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 600 }}>
                      {modalCard.acctId}
                    </span>
                    <Button variant="link" size="sm" onClick={() => handleCopyAcct(modalCard.acctId)}>
                      {copied ? 'Copied' : 'Copy account #'}
                    </Button>
                  </div>
                </div>
                <div>
                  <DetailLabel text="Card number" />
                  <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12, color: 'var(--ink-500)' }}>
                    {modalCard.cardNumMasked ?? '—'}
                  </div>
                </div>
                {modalCard.cdRecordId && (
                  <div>
                    <DetailLabel text="CD Record #" />
                    <div style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 600 }}>
                      {modalCard.cdRecordId}
                    </div>
                  </div>
                )}
                {modalCard.atmAcctId && (
                  <div>
                    <DetailLabel text="ATM module acct" />
                    <div style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 600 }}>
                      {modalCard.atmAcctId}
                    </div>
                  </div>
                )}
                <div>
                  <DetailLabel text="Embossed name" />
                  <div>{modalCard.embosName}</div>
                </div>
              </div>

              {/* Entrust workflow steps (verbatim from SPFx, Collette 3/27/2026) */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--sp-3)',
                    marginBottom: 'var(--sp-3)',
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                      color: 'var(--pcb-blue)',
                    }}
                  >
                    Entrust workflow
                  </div>
                  {/* Branch-specific launch (WT-BACKLOG B1). Copies the account
                      number first so step 2 (paste) is one action, then opens
                      the ACTIVE branch's Entrust link. Hidden — with a manual-
                      steps note — when the branch has no URL configured yet. */}
                  {entrustUrl ? (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => { handleCopyAcct(modalCard.acctId); openEntrust(); }}
                    >
                      Open Entrust ({branchLabel})
                    </Button>
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--ink-500)', fontStyle: 'italic' }}>
                      No Entrust link set for {branchLabel} — open Entrust manually
                    </span>
                  )}
                </div>
                {ENTRUST_STEPS(modalCard.acctId, modalCard.cardDesignName).map((step, idx, arr) => (
                  <div
                    key={step}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 'var(--sp-4)',
                      padding: 'var(--sp-2) 0',
                      borderBottom: idx < arr.length - 1 ? '1px solid var(--ink-100)' : 'none',
                      fontSize: 13,
                    }}
                  >
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: 'var(--brand-fill)',
                        color: 'var(--on-brand)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {idx + 1}
                    </span>
                    <span>{step}</span>
                  </div>
                ))}
              </div>

              <Banner tone="warn">
                After the card prints successfully, click <strong>&ldquo;Card Printed — Activate
                Now&rdquo;</strong> below. This will set the card status to Active in Core Director.
                {modalCard.isMinor && (
                  <> Minor cardholder — limits post at <strong>$510 ATM / $510 POS</strong> (Collette 4/7/2026).</>
                )}
              </Banner>

              <Banner tone="info">
                After activation, give the customer information to call{' '}
                <strong>1-800-992-3808</strong> to select their own PIN.
              </Banner>

              {activateError && (
                <Banner tone="err">Activation failed: {activateError}</Banner>
              )}
            </div>
          )}
        </ModalShell>
      )}
    </div>
  );
}

function DetailLabel({ text }: { text: string }): JSX.Element {
  return (
    <div
      style={{
        fontSize: 10,
        color: 'var(--ink-500)',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        marginBottom: 2,
      }}
    >
      {text}
    </div>
  );
}
